import { useState, useEffect, useRef, useCallback } from 'react';
import {
  getFirestoreInstance,
  getAuthInstance,
  collection,
  query,
  orderBy,
  onSnapshot,
  limit,
} from '@/lib/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import type { Firestore } from 'firebase/firestore';

export interface ChatNotification {
  leagueId: number;
  leagueName: string;
  themeColor?: string;
  unreadCount: number;
  lastMessageAt: Date | null;
  lastMessagePreview: string | null;
  lastMessageAuthor: string | null;
}

interface UseChatNotificationsOptions {
  leagues: Array<{
    id: number;
    name: string;
    themeColor?: string;
  }>;
  enabled?: boolean;
}

interface UseChatNotificationsReturn {
  chatNotifications: ChatNotification[];
  totalUnreadCount: number;
  isLoading: boolean;
  markLeagueAsRead: (leagueId: number) => void;
}

// Local storage key for storing last read timestamps per league
const getStorageKey = (leagueId: number, userId: string) =>
  `yapzap_chat_lastread_${leagueId}_${userId}`;

/**
 * Hook to track chat notifications across multiple leagues
 */
export function useChatNotifications({
  leagues,
  enabled = true,
}: UseChatNotificationsOptions): UseChatNotificationsReturn {
  const [chatNotifications, setChatNotifications] = useState<ChatNotification[]>([]);
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const firestoreRef = useRef<Firestore | null>(null);
  const unsubscribeRefs = useRef<Map<number, () => void>>(new Map());

  // Get current user ID from Firebase Auth
  useEffect(() => {
    let isMounted = true;

    const setupAuth = async () => {
      try {
        const auth = await getAuthInstance();
        const unsubAuth = onAuthStateChanged(auth, (user) => {
          if (isMounted) {
            setCurrentUserId(user?.uid || null);
          }
        });
        return unsubAuth;
      } catch (err) {
        console.error('Chat notifications auth setup error:', err);
        return () => {};
      }
    };

    let unsubAuthFn: (() => void) | null = null;
    setupAuth().then(unsub => {
      if (isMounted) {
        unsubAuthFn = unsub;
      } else {
        unsub();
      }
    });

    return () => {
      isMounted = false;
      if (unsubAuthFn) {
        unsubAuthFn();
      }
    };
  }, []);

  // Set up listeners for each league's chat
  useEffect(() => {
    if (!enabled || !currentUserId || leagues.length === 0) {
      setIsLoading(false);
      return;
    }

    let isMounted = true;

    const setupListeners = async () => {
      try {
        const firestore = await getFirestoreInstance();

        if (!firestore) {
          setIsLoading(false);
          return;
        }
        firestoreRef.current = firestore;

        // Clear previous listeners
        unsubscribeRefs.current.forEach(unsub => unsub());
        unsubscribeRefs.current.clear();

        const notifications: Map<number, ChatNotification> = new Map();

        // Set up a listener for each league
        for (const league of leagues) {
          const messagesRef = collection(firestore, `leagueChats/${league.id}/messages`);
          const messagesQuery = query(
            messagesRef,
            orderBy('createdAt', 'desc'),
            limit(50) // Only check recent messages
          );

          const unsubscribe = onSnapshot(
            messagesQuery,
            (snapshot) => {
              if (!isMounted) return;

              // Get last read timestamp from localStorage
              // If no timestamp stored, use 24 hours ago as default to show recent messages
              const storageKey = getStorageKey(league.id, currentUserId);
              const storedTimestamp = localStorage.getItem(storageKey);
              const defaultLastRead = new Date(Date.now() - 24 * 60 * 60 * 1000); // 24 hours ago
              const lastRead = storedTimestamp ? new Date(storedTimestamp) : defaultLastRead;

              let unreadCount = 0;
              let lastMessageAt: Date | null = null;
              let lastMessagePreview: string | null = null;
              let lastMessageAuthor: string | null = null;
              let capturedFirst = false;

              snapshot.forEach((doc) => {
                const data = doc.data();
                const createdAt = data.createdAt?.toDate();

                // Get the most recent message info (query is ordered desc, so first doc is newest)
                if (!capturedFirst && createdAt) {
                  capturedFirst = true;
                  lastMessageAt = createdAt;
                  lastMessagePreview = data.content?.slice(0, 50) || null;
                  lastMessageAuthor = data.username || null;
                }

                // Count unread messages (not from current user, after last read)
                if (createdAt && createdAt > lastRead && data.userId !== currentUserId) {
                  unreadCount++;
                }
              });

              notifications.set(league.id, {
                leagueId: league.id,
                leagueName: league.name,
                themeColor: league.themeColor,
                unreadCount,
                lastMessageAt,
                lastMessagePreview,
                lastMessageAuthor,
              });

              // Update state with all notifications (all leagues, not just unread ones)
              const notificationsList = Array.from(notifications.values())
                .sort((a, b) => {
                  if (!a.lastMessageAt && !b.lastMessageAt) return 0;
                  if (!a.lastMessageAt) return 1;
                  if (!b.lastMessageAt) return -1;
                  return b.lastMessageAt.getTime() - a.lastMessageAt.getTime();
                });

              setChatNotifications(notificationsList);
              setTotalUnreadCount(notificationsList.reduce((sum, n) => sum + n.unreadCount, 0));
              setIsLoading(false);
            },
            (err) => {
              console.error(`Chat notification listener error for league ${league.id}:`, err);
            }
          );

          unsubscribeRefs.current.set(league.id, unsubscribe);
        }
      } catch (err) {
        if (!isMounted) return;
        console.error('Chat notifications setup error:', err);
        setIsLoading(false);
      }
    };

    setupListeners();

    return () => {
      isMounted = false;
      unsubscribeRefs.current.forEach(unsub => unsub());
      unsubscribeRefs.current.clear();
    };
  }, [leagues, enabled, currentUserId]);

  // Mark a league's chat as read
  const markLeagueAsRead = useCallback((leagueId: number) => {
    if (!currentUserId) return;

    const now = new Date();
    const storageKey = getStorageKey(leagueId, currentUserId);
    localStorage.setItem(storageKey, now.toISOString());

    // Update local state
    setChatNotifications(prev => prev.filter(n => n.leagueId !== leagueId));
    setTotalUnreadCount(prev => {
      const notification = chatNotifications.find(n => n.leagueId === leagueId);
      return prev - (notification?.unreadCount || 0);
    });
  }, [currentUserId, chatNotifications]);

  return {
    chatNotifications,
    totalUnreadCount,
    isLoading,
    markLeagueAsRead,
  };
}
